import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

const SOCIALS = [
  { id: "google", label: "Sign up with Google", color: "#fff", text: "#0F172A", border: "#E2E8F0" },
  { id: "apple", label: "Sign up with Apple", color: "#000", text: "#fff" },
  { id: "linkedin", label: "Sign up with LinkedIn", color: "#0A66C2", text: "#fff" },
];

const ROLES = [
  { v: "investor", label: "Investor" },
  { v: "buyer", label: "Buyer" },
  { v: "seller", label: "Seller" },
  { v: "founder", label: "Founder" },
  { v: "advisor", label: "Advisor" },
];

function SocialBtn({ s }) {
  return (
    <button type="button" onClick={() => toast.info(`${s.id.charAt(0).toUpperCase() + s.id.slice(1)} signup coming soon`)} data-testid={`social-${s.id}`}
      className="relative h-12 rounded-xl font-medium flex items-center justify-center w-full hover:opacity-90 transition-opacity"
      style={{ background: s.color, color: s.text, border: s.border ? `1.5px solid ${s.border}` : "none" }}>
      <span className="text-sm">{s.label}</span>
      <span className="absolute right-3 top-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Soon</span>
    </button>
  );
}

export default function SignupPage() {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", password: "", role: "investor" });
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await register(f);
      toast.success("Welcome to DealVest!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    }
  };

  return (
    <div data-testid="signup-page" className="min-h-[calc(100vh-160px)] flex items-center py-10">
      <div className="max-w-md mx-auto w-full px-5">
        <div className="text-center mb-8">
          <h1 className="dv-display text-3xl md:text-4xl mb-2">Create your account</h1>
          <p className="text-slate-500">Free · 30 seconds · no credit card</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 dv-shadow-soft">
          <div className="space-y-2.5 mb-5">
            {SOCIALS.map(s => <SocialBtn key={s.id} s={s} />)}
          </div>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">or with email</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="dv-label">Full name</label>
              <input required value={f.full_name} onChange={e => setF({ ...f, full_name: e.target.value })} className="dv-input" placeholder="Jane Doe" data-testid="signup-name" />
            </div>
            <div>
              <label className="dv-label">Email</label>
              <input type="email" required value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="dv-input" placeholder="you@example.com" data-testid="signup-email" />
            </div>
            <div>
              <label className="dv-label">Password</label>
              <div className="relative">
                <input type={show ? "text" : "password"} required value={f.password} onChange={e => setF({ ...f, password: e.target.value })} className="dv-input pr-12" placeholder="At least 6 characters" data-testid="signup-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <div>
              <label className="dv-label">I am a</label>
              <div className="grid grid-cols-5 gap-2">
                {ROLES.map(r => (
                  <button key={r.v} type="button" onClick={() => setF({ ...f, role: r.v })}
                    data-testid={`signup-role-${r.v}`}
                    className={`h-11 text-xs font-semibold rounded-lg border transition-all ${f.role === r.v ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={loading} className="dv-btn dv-btn-primary w-full" data-testid="signup-submit">
              {loading ? "Creating account..." : "Create free account"} <ArrowRight size={16} />
            </button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">
            Already a member? <Link to="/login" className="text-blue-600 font-semibold">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
